import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalHeader } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { MeiliTrackData } from '../../music24/components/SearchField/types';
import {
  CatalogClaimResponse,
  CatalogItem,
  CatalogMergeCandidate,
  CatalogSuggestion,
} from '../../../redux/api/fymAPI.types';
import { ClaimRoute } from './claimWay/useClaimRoute';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { ClaimMergePrompt } from './merge/ClaimMergePrompt';
import { mergeMessages } from './merge/mergeMessages';
import { CatalogClaimSnapshot } from './types';
import { ClaimSearchStep } from './ClaimSearchStep';
import { ClaimConfirmStep } from './ClaimConfirmStep';
import { MasterStep } from './song/MasterStep';

interface ClaimReleaseModalProps {
  isOpen: boolean;
  accountId: number;
  /** The account's own name, used when the catalog credits nobody. */
  fallbackArtistName: string;
  /** Set when the sheet was opened from a suggestion, so it starts at step two. */
  initialCatalogTrackMaid?: string;
  /**
   * Set when that suggestion came off the artist's own profile. There is no
   * catalog track to read for one of those, so it arrives with its details.
   */
  initialSnapshot?: CatalogClaimSnapshot;
  /** The suggestion that the sheet opened on: the claim can merge into a song (design catalog-merge c2). */
  suggestion?: CatalogSuggestion;
  /** The address of the sheet (the way and the picked row of the merge). */
  claimRoute?: ClaimRoute;
  closeModal: () => void;
  /** The way out when our catalog does not hold a release that is already out. */
  onAddRelease: () => void;
  onAddUnreleased: () => void;
}

/**
 * Claim a release: search the catalog, then confirm what you are claiming.
 *
 * Two steps rather than one tap in a list, because the second one is where the
 * artist reads what a claim does and does not buy.
 */
const ClaimReleaseModalComponent: React.FC<ClaimReleaseModalProps> = ({
  isOpen,
  accountId,
  fallbackArtistName,
  initialCatalogTrackMaid,
  initialSnapshot,
  suggestion,
  claimRoute,
  closeModal,
  onAddRelease,
  onAddUnreleased,
}) => {
  const intl = useIntl();
  const [catalogTrackMaid, setCatalogTrackMaid] = useState<string | undefined>(initialCatalogTrackMaid);
  const [snapshot, setSnapshot] = useState<CatalogClaimSnapshot | undefined>(initialSnapshot);
  // design k1: after the claim, step 2 asks for the master of the saved row.
  const [claimedItem, setClaimedItem] = useState<CatalogItem>();
  // design catalog-merge a4: the claim looks like rows that are there already.
  const [mergeQuestion, setMergeQuestion] = useState<{ item: CatalogItem; candidates: CatalogMergeCandidate[] }>();
  const { showSuccess } = useShowToast();

  // Re-opening the sheet starts it over, and opening it from a suggestion
  // starts it on that suggestion.
  useEffect(() => {
    setCatalogTrackMaid(initialCatalogTrackMaid);
    setSnapshot(initialSnapshot);
    setClaimedItem(undefined);
    setMergeQuestion(undefined);
  }, [initialCatalogTrackMaid, initialSnapshot, isOpen]);

  // A row that has a master already (a link to an unreleased row with one) needs no step 2.
  const askForMaster = useCallback(
    (item: CatalogItem) => {
      if (item.song.hasMaster) {
        closeModal();
        return;
      }
      setClaimedItem(item);
    },
    [closeModal]
  );
  const onClaimed = useCallback(
    ({ item, mergedIntoUuid, mergeCandidates }: CatalogClaimResponse) => {
      if (mergedIntoUuid !== undefined) {
        // A sure match joined the song the artist made (design catalog-merge a5).
        showSuccess(intl.formatMessage(mergeMessages.autoDone, { title: item.title }));
      } else if (mergeCandidates !== undefined && mergeCandidates.length > 0) {
        setMergeQuestion({ item, candidates: mergeCandidates });
        return;
      }
      askForMaster(item);
    },
    [askForMaster, intl, showSuccess]
  );
  // design catalog-merge c2: the release joined a song of the catalog. No master step: the song has its own.
  const onMerged = useCallback(
    ({ item }: CatalogClaimResponse) => {
      showSuccess(intl.formatMessage(mergeMessages.done, { from: snapshot?.title ?? item.title, title: item.title }));
      closeModal();
    },
    [closeModal, intl, showSuccess, snapshot?.title]
  );
  const onMergeAnswered = useCallback(
    (kept: CatalogItem) => {
      setMergeQuestion(undefined);
      askForMaster(kept);
    },
    [askForMaster]
  );

  // A track picked out of the search is a catalog track, so the details of the
  // profile release the sheet may have opened on no longer apply.
  const onTrackPicked = useCallback((track: MeiliTrackData) => {
    setSnapshot(undefined);
    setCatalogTrackMaid(track.id);
  }, []);

  const onBack = useCallback(() => {
    setSnapshot(undefined);
    setCatalogTrackMaid(undefined);
  }, []);

  return (
    <Modal isOpen={isOpen} toggle={closeModal} centered size="lg" className="sml-modal">
      <ModalHeader toggle={closeModal}>
        {intl.formatMessage({ id: 'app.sml.catalog.claim.title', defaultMessage: 'Claim a release' })}
      </ModalHeader>
      {mergeQuestion !== undefined && (
        <ClaimMergePrompt
          accountId={accountId}
          item={mergeQuestion.item}
          candidates={mergeQuestion.candidates}
          onDone={onMergeAnswered}
        />
      )}
      {mergeQuestion === undefined && claimedItem !== undefined && (
        <MasterStep accountId={accountId} item={claimedItem} firstStep="claimed" onDone={closeModal} />
      )}
      {mergeQuestion === undefined && claimedItem === undefined && catalogTrackMaid === undefined && (
        <ClaimSearchStep
          onTrackPicked={onTrackPicked}
          onAddRelease={onAddRelease}
          onAddUnreleased={onAddUnreleased}
          closeModal={closeModal}
        />
      )}
      {mergeQuestion === undefined && claimedItem === undefined && catalogTrackMaid !== undefined && (
        <ClaimConfirmStep
          accountId={accountId}
          catalogTrackMaid={catalogTrackMaid}
          presetSnapshot={snapshot}
          fallbackArtistName={fallbackArtistName}
          onBack={onBack}
          onClaimed={onClaimed}
          suggestion={suggestion}
          claimRoute={claimRoute}
          onMerged={onMerged}
        />
      )}
    </Modal>
  );
};

export const ClaimReleaseModal = TypedMemo(ClaimReleaseModalComponent);
